import { logger } from '@web_opxp/features';
import { createContext, FC, PropsWithChildren, useCallback, useContext, useRef, useState } from 'react';

type TSimpleCallback = () => void;

interface INavigationBlockStore {
  isBlocked: boolean;
  blockNavigation: (handler: TSimpleCallback) => void;
  unblockNavigation: TSimpleCallback;
  blockedCallback: TSimpleCallback;
}

const NavigationContext = createContext<INavigationBlockStore | undefined>(undefined);

export const NavigationBlockProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isBlocked, setIsBlocked] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const handler = useRef<TSimpleCallback>();
  const linkBlockHandler = useCallback(
    (e: MouseEvent) => {
      logger(['linkBlockHandler', isBlocked]);
      if (!isBlocked && !handler.current) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (handler.current) {
        handler.current();
      }
    },
    [isBlocked]
  );

  const visibilityBlockHandler = useCallback(
    (e: Event) => {
      if (!isBlocked) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (handler.current) {
        handler.current();
      }
    },
    [isBlocked]
  );

  const blockNavigation = (_handler: TSimpleCallback) => {
    logger(['block navigation', disabled, isBlocked]);
    handler.current = _handler;
    if (disabled || isBlocked) {
      return;
    }
    const links = document.getElementsByTagName('a');
    for (let index = 0; index < links.length; index++) {
      const element = links.item(index);
      if (!element) {
        continue;
      }
      element.addEventListener('click', linkBlockHandler);
    }
    document.addEventListener('visibilitychange', visibilityBlockHandler);
    setIsBlocked(true);
  };

  const unblockNavigation = () => {
    logger(['unblock navigation', disabled, isBlocked]);
    const links = document.getElementsByTagName('a');
    for (let index = 0; index < links.length; index++) {
      const element = links.item(index);
      if (!element) {
        continue;
      }
      element.removeEventListener('click', linkBlockHandler);
    }
    document.removeEventListener('visibilitychange', visibilityBlockHandler);
    handler.current = undefined;
    setIsBlocked(false);
    setDisabled(true);
  };

  function blockedCallback() {
    logger(['blockedCallback', isBlocked, !!handler.current]);
    if (isBlocked && handler.current) {
      handler.current();
    }
  }

  const store: INavigationBlockStore = {
    isBlocked,
    blockNavigation,
    unblockNavigation,
    blockedCallback,
  };
  return <NavigationContext.Provider value={store}>{children}</NavigationContext.Provider>;
};

export default function useNavigationBlocker() {
  const value = useContext(NavigationContext);
  if (!value) {
    throw new Error('This hook must be used only by children of NavigationBlockProvider');
  }
  return value;
}
